import type { MouseEvent } from 'react';
import { CheckCircleOutline, StarFill, StarOutline } from 'antd-mobile-icons';
import styled, { css } from 'styled-components';
import { r } from '../../theme/riderTheme';

interface QueuedPackActionsProps {
  packName: string;
  isDesignatedNext: boolean;
  canActivateNow: boolean;
  onActivate: (event: MouseEvent<HTMLButtonElement>) => void;
  onToggleNext: (event: MouseEvent<HTMLButtonElement>) => void;
}

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  padding: 0 16px 14px;
`;

const RowAction = styled.button.attrs({ type: 'button' as const })<{ $prominent?: boolean }>`
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 999px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background 160ms ease, border-color 160ms ease;

  ${({ theme, $prominent }) => $prominent
    ? css`
        background: ${r(theme).surface.raisedSelected};
        border: 1px solid ${r(theme).accent.limeDeep};
        color: ${r(theme).accent.ok};

        &:hover {
          background: ${r(theme).glow.selectionLime};
        }
      `
    : css`
        background: transparent;
        border: 1px solid ${r(theme).surface.edge};
        color: ${r(theme).text.primary};

        &:hover {
          background: ${r(theme).surface.inputIdle};
        }
      `}

  &:hover {
    border-color: ${({ theme }) => r(theme).surface.edgeStrong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

export const QueuedPackActions = ({
  packName,
  isDesignatedNext,
  canActivateNow,
  onActivate,
  onToggleNext,
}: QueuedPackActionsProps) => (
  <ActionRow>
    {canActivateNow && (
      <RowAction
        $prominent
        onClick={onActivate}
        aria-label={`Activate "${packName}" now`}
      >
        <CheckCircleOutline style={{ fontSize: '10px' }} />
        Activate Now
      </RowAction>
    )}
    <RowAction
      onClick={onToggleNext}
      aria-label={
        isDesignatedNext
          ? `Remove "${packName}" as next pack`
          : `Set "${packName}" as next pack`
      }
    >
      {isDesignatedNext ? (
        <>
          <StarFill style={{ fontSize: '10px' }} />
          Next
        </>
      ) : (
        <>
          <StarOutline style={{ fontSize: '10px' }} />
          Set as next
        </>
      )}
    </RowAction>
  </ActionRow>
);

export default QueuedPackActions;
